/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

// client/src/components/ThemeSelector.jsx
import React, { useState } from 'react';
import { useBranding } from '../contexts/BrandingContext';
import { Button } from './ui/button';
import { Palette, Check } from 'lucide-react';

const ThemeSelector = () => {
  const { theme, setTheme, themes } = useBranding();
  const [isOpen, setIsOpen] = useState(false);

  const handleThemeSelect = (themeName) => {
    setTheme(themeName);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <Button
        onClick={() => setIsOpen(!isOpen)}
        variant="ghost"
        size="sm"
        className="text-white hover:bg-white/10 flex items-center gap-2"
      >
        <Palette size={18} />
        <span className="hidden sm:inline">Theme</span>
      </Button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Menu */}
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl z-50 overflow-hidden border border-gray-200">
            <div className="p-3 bg-gray-50 border-b border-gray-200">
              <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                <Palette size={16} />
                Select Theme
              </h3>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {Object.entries(themes).map(([themeName, themeColors]) => (
                <button
                  key={themeName}
                  onClick={() => handleThemeSelect(themeName)}
                  className={`w-full px-4 py-3 hover:bg-gray-50 transition-colors ${
                    theme === themeName ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Color Preview */}
                      <div className="flex gap-1">
                        <div
                          className="w-4 h-4 rounded-full border border-gray-300"
                          style={{ backgroundColor: themeColors.primary }}
                          title="Primary"
                        />
                        <div
                          className="w-4 h-4 rounded-full border border-gray-300"
                          style={{ backgroundColor: themeColors.secondary }}
                          title="Secondary"
                        />
                        <div
                          className="w-4 h-4 rounded-full border border-gray-300"
                          style={{ backgroundColor: themeColors.accent }}
                          title="Accent"
                        />
                      </div>

                      {/* Theme Name */}
                      <div className="flex flex-col items-start">
                        <span className="text-sm font-medium text-gray-700">
                          {themeColors.name}
                        </span>
                        {themeName === 'custom' && (
                          <span className="text-xs text-gray-500">
                            Uses colors from System Config
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Check Mark for Active Theme */}
                    {theme === themeName && (
                      <Check size={16} className="text-blue-600" />
                    )}
                  </div>
                </button>
              ))}
            </div>

            <div className="p-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 text-center">
              Theme persists across sessions
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ThemeSelector;